import {
  EMPLOYMENT_CLA_YEAR,
  EMPLOYMENT_HOURS,
  EMPLOYMENT_HOURS_CORRECTION,
  WORK_AGREEMENTS_BALANCE_BUDGET
} from 'RestAPIs/endpoints';

import axios from 'axios';

import type {
  ClaYearInfo,
  EmploymentHoursRow
} from 'components/employee-card/employment/EmploymentHours/EmploymentHours.types';

/**
 * These two calls deliberately use the legacy axios layer rather than a generated Rapini hook.
 *
 * The backend returns `List<Map<String, Object>>` for /hours (EmployeeDelegate.groovy:551) — there is
 * no DTO to generate a schema from — and adding either path to api.internal.yaml would delete the
 * existing Groovy route, which four Dojo call sites still consume (including EmployeeHoursWidget via
 * EmployeeHoursViewModel.js:55, which is outside this migration). Moving these to OpenAPI is tracked
 * as follow-up work and must wait until those Dojo consumers are gone.
 */
export const getEmploymentHours = (
  employeeId: number,
  workAgreementId: number,
  year: number
) =>
  axios
    .get<EmploymentHoursRow[]>(EMPLOYMENT_HOURS, {
      params: { year },
      pathParams: { employeeId, workAgreementId }
    })
    .then((response) => response.data);

export const getEmploymentClaYear = (
  employeeId: number,
  workAgreementId: number,
  year: number
) =>
  axios
    .get<ClaYearInfo>(EMPLOYMENT_CLA_YEAR, {
      params: { year },
      pathParams: { employeeId, workAgreementId }
    })
    .then((response) => response.data);

/**
 * Saves one period's corrections. The backend keeps only the 34 keys in `MNG_CORRECTION_FIELDS`
 * (`EmployeeDelegate.groovy:90-125`) and updates only the keys present, so an omitted field keeps its
 * stored value — which is why the payload is stripped of empty values rather than sent in full.
 */
export const saveHoursCorrection = (
  employeeId: number,
  workAgreementId: number,
  dutyLineMngId: number,
  correction: Record<string, unknown>
) =>
  axios.put(EMPLOYMENT_HOURS_CORRECTION, correction, {
    pathParams: { employeeId, workAgreementId, dutyLineMngId }
  });

/** The three "deposited" totals shown read-only in the last period's correction dialog. */
export const getBalanceBudgetYearOverview = (
  workAgreementId: number,
  year: number
) =>
  axios
    .get<{
      savedPlusHours?: number;
      savedExtraStatutoryLeaveHours?: number;
      savedPLBHours?: number;
    }>(`${WORK_AGREEMENTS_BALANCE_BUDGET}/:year`, {
      pathParams: { workAgreementId, year }
    })
    .then((response) => response.data);
